import { Collapse } from "antd";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useCallback, useContext, useMemo } from "react";
import { QUERY_GET_DB_TEMPLATES } from "services/graphQL/queries";
import ErrorBoundary from "components/error-boundary";
import { FEATURE_WORKFLOW_INSTANCE_BY_IDS } from "entity-app/graphQL/ciq-feature-queries";
import { EWorkflowStatusDataBlock } from "../../constants";
import PanelItemNewModel from "./panel-item-new-model";
import { FeatureWorkflowInstance, PropsEditDuration } from "./definitions";

const labelsByFeatureTypeId: any = {
  [FeatureTypes.BID_PACKAGE]: {
    selectedRowsLabel: "Selected Bid Packages:"
  },
  [FeatureTypes.SUBMITTALS]: {
    selectedRowsLabel: "Selected Submittals:"
  },
  [FeatureTypes.MATERIALS]: {
    selectedRowsLabel: "Selected Materials:"
  }
};

// merge the props with the new props
function FeatureBulkEditDurationsNewModel(props: PropsEditDuration) {
  const { selectedRows, featureTypeId, onUpdateDone } = props;

  const { gqlClientForProject }: TProjectContext = useContext(ProjectContext);

  const { data: DBTemplatesData } = useCIQQuery(QUERY_GET_DB_TEMPLATES, {
    client: gqlClientForProject,
    skip: !gqlClientForProject,
    variables: {
      feature_id: featureTypeId
    }
  });

  const {
    data: featureWorkflowInstanceData,
    refetch: refetchFeatureWorkflowInstanceData
  }: { data: any; refetch: any } = useCIQQuery(
    FEATURE_WORKFLOW_INSTANCE_BY_IDS,
    {
      client: gqlClientForProject,
      skip: !gqlClientForProject,
      variables: { featureIds: selectedRows.map((row) => row.id) }
    }
  );

  const selectedRowsWithWorkflowData: any = useMemo(() => {
    if (!featureWorkflowInstanceData) return [];

    const rowsWithWFInstance = selectedRows
      .filter((row: any) => {
        return row?.workflow_status !== EWorkflowStatusDataBlock.WF_COMPLETED;
      })
      .map((row) => {
        const workflowInstance =
          featureWorkflowInstanceData.feature_workflow_instance.find(
            (instance: FeatureWorkflowInstance) =>
              instance.feature_instance.id === row.id
          );
        return { ...row, workflowInstance };
      });

    return rowsWithWFInstance;
  }, [featureWorkflowInstanceData, selectedRows]);

  const DBTemplates = useMemo(() => {
    if (!DBTemplatesData) return null;
    return DBTemplatesData.project_workflow_templates;
  }, [DBTemplatesData]);

  const templateWithSelectedFeatures = useMemo(() => {
    if (!DBTemplates || !featureWorkflowInstanceData) return null;
    return DBTemplates.map((mapTemplate: any) => {
      const template: any = { ...mapTemplate };
      template.features = [];
      selectedRowsWithWorkflowData.forEach((row: any) => {
        if (template.id === row.workflowInstance.workflow_template_id) {
          template.features.push(row);
        }
      });
      return template;
    });
  }, [DBTemplates, featureWorkflowInstanceData, selectedRowsWithWorkflowData]);

  const getDefaultActiveKey = useCallback(() => {
    if (!templateWithSelectedFeatures) return undefined;
    const templateWithFeatures = templateWithSelectedFeatures.reduce(
      (acc: any, template: any) => {
        if (template.features.length > 0) {
          acc.push(template.id);
        }
        return acc;
      },
      []
    );
    if (templateWithFeatures.length === 1) {
      return templateWithFeatures[0];
    }
    return undefined;
  }, [templateWithSelectedFeatures]);

  return (
    <div className="px-2 -mt-2" data-testid="bulk-edit-durations-root">
      <div className="font-semibold" data-testid="selected-rows-label">
        <span className="tracking-wide">
          {labelsByFeatureTypeId[featureTypeId].selectedRowsLabel}
        </span>{" "}
        {selectedRows.length}
      </div>

      {templateWithSelectedFeatures &&
        templateWithSelectedFeatures?.length !== 0 && (
          <div className="mt-2" data-testid="info-completed-milestones">
            Durations for completed milestones will not be updated.
          </div>
        )}
      {templateWithSelectedFeatures &&
        templateWithSelectedFeatures?.length === 0 && (
          <div className="mt-2 font-semibold" data-testid="info-all-closed">
            Durations cannot be edited as all the selected materials are closed.
          </div>
        )}

      {templateWithSelectedFeatures &&
        templateWithSelectedFeatures?.length !== 0 && (
          <div className="mt-4" data-testid="collapse-container">
            <ErrorBoundary>
              <Collapse
                className="ciq-collapse"
                expandIconPosition="end"
                defaultActiveKey={getDefaultActiveKey()}
                data-testid="template-collapse"
              >
                {templateWithSelectedFeatures?.map((template: any) => {
                  if (!template.features.length) return null;
                  return (
                    <PanelItemNewModel
                      template={template}
                      key={template.id}
                      featureTypeId={featureTypeId}
                      onSaveSuccess={() => {
                        refetchFeatureWorkflowInstanceData();
                        onUpdateDone();
                      }}
                      data-testid={`panel-item-${template.id}`}
                    />
                  );
                })}
              </Collapse>
            </ErrorBoundary>
          </div>
        )}
    </div>
  );
}

export default FeatureBulkEditDurationsNewModel;
